import { ChevronDown } from "baseui/icon";
import classNames from "classnames";
import moment from "moment";
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useState,
} from "react";
import { IoRepeat } from "react-icons/io5";

import { AvatarPlaceholder } from "../../../components/avatar/AvatarPlaceholder";
import { AvatarStack } from "../../../components/avatar/AvatarStack";
import {
  getInitials,
  getInitialsFromFullName,
  showStatus,
} from "../../../constants/constants";
import {
  HomeFilter,
  HomeTab,
} from "../../../factories/works/models/home-enums";
import { WorkModel } from "../../../factories/works/models/work.model";
import { navigateToContact } from "../../contacts/utils/navigateToContacts";
import { StatusesDropdown } from "../dropdown/StatusesDropdown";
import { getStatusColor } from "../dropdown/utils";
import { getWorkProgress } from "../utils/getWorkProgress";

type Props = {
  work: WorkModel;
  index: number;
  worksCount: number;
  myWorkHome?: boolean;
  filter?: HomeTab | HomeFilter.OverDue;
  archiveWorks?: WorkModel[];
  setArchiveWorks?: Dispatch<SetStateAction<WorkModel[]>>;
  statusFilter?: string[];
  showSelection?: boolean;
  showTaskProgress?: boolean;
  showTimeBudget?: boolean;
  showRole?: boolean;
  /** Recurring-icon column (hidden on a work item's Repeat tab). */
  showRepeat?: boolean;
  columnsCount: number;
  onNavigateToWork: (workId: string) => void;
  onNavigateToContact: (link: string) => void;
  renderExpandedContent?: (work: WorkModel) => ReactNode;
  /** The work item being viewed (Repeat tab): highlighted with a "Current" tag. */
  isCurrent?: boolean;
};

const TASK_PROGRESS_COLOR = "#8BD8EC";
const getClampedPercent = (value: number): number =>
  Math.min(Math.max(value, 0), 100);

export const getBudgetColor = (usedPercent: number): string => {
  if (usedPercent <= 60) {
    return "#2FC66E";
  }

  if (usedPercent <= 80) {
    return "#F4C20D";
  }

  return "#EF3B3B";
};

export const getTimeBudgetProgress = (work: WorkModel): number => {
  const budgetedHours = Number(work.budgetedTime);
  const totalDurationSeconds = Number(work.totalDuration);

  if (
    !Number.isFinite(budgetedHours) ||
    budgetedHours <= 0 ||
    !Number.isFinite(totalDurationSeconds)
  ) {
    return 0;
  }

  return Math.round((totalDurationSeconds / (budgetedHours * 3600)) * 100);
};

const getSubtasksCountText = ({
  count,
  emptyText,
  singularSuffix,
  pluralSuffix = singularSuffix,
}: {
  count: number;
  emptyText: string;
  singularSuffix: string;
  pluralSuffix?: string;
}): string => {
  if (count === 0) {
    return emptyText;
  }

  const subtaskLabel = count === 1 ? "subtask" : "subtasks";
  const suffix = count === 1 ? singularSuffix : pluralSuffix;

  return `${count} ${subtaskLabel} ${suffix}`;
};

const getDueDateWord = (
  selectedFilter: HomeTab | HomeFilter.OverDue,
  work: WorkModel,
): string => {
  const count = work.taskCounts - work.completedTaskCounts;

  if (selectedFilter === "ThisWeek") {
    return getSubtasksCountText({
      count,
      emptyText: "No subtasks to do this week",
      singularSuffix: "to do this week",
    });
  }

  if (selectedFilter === "NextWeek") {
    return getSubtasksCountText({
      count,
      emptyText: "No subtasks to do next week",
      singularSuffix: "to do next week",
    });
  }

  return getSubtasksCountText({
    count,
    emptyText: "No subtasks to do later",
    singularSuffix: "to do later",
  });
};

const getMessage = (counts: number): string => {
  return getSubtasksCountText({
    count: counts,
    emptyText: "No subtasks to do",
    singularSuffix: "to do",
    pluralSuffix: "to do",
  });
};

const isOverdue = (work: WorkModel): boolean => {
  return moment().startOf("day").unix() > moment(work.dueDate).unix();
};

type ProgressCellProps = {
  percent: number;
  color: string;
};

const ProgressCell: FC<ProgressCellProps> = ({ percent, color }) => {
  const safePercent = getClampedPercent(percent);

  return (
    <div className={"flex w-[100px] flex-col items-center"}>
      <div className={"font-semibold text-[#6B7280]"}>{safePercent}%</div>
      <div className={"h-[5px] w-[100px] rounded-[10px] bg-[#9FA8B4]"}>
        <div
          style={{
            width: `${safePercent}%`,
            backgroundColor: color,
          }}
          className={"h-[5px] rounded-[10px]"}
        />
      </div>
    </div>
  );
};

const TimeBudgetCell: FC<{ work: WorkModel }> = ({ work }) => {
  const timeBudgetProgress = getTimeBudgetProgress(work);

  return (
    <ProgressCell
      percent={timeBudgetProgress}
      color={getBudgetColor(timeBudgetProgress)}
    />
  );
};

export const WorkRow: FC<Props> = ({
  work,
  index,
  worksCount,
  myWorkHome,
  filter,
  archiveWorks,
  setArchiveWorks,
  statusFilter,
  showSelection = true,
  showTaskProgress = true,
  showTimeBudget = true,
  showRole = false,
  showRepeat = true,
  columnsCount,
  onNavigateToWork,
  onNavigateToContact,
  renderExpandedContent,
  isCurrent = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasExpandedContent = Boolean(renderExpandedContent);

  const handleToggleExpanded = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <>
      <tr
        onClick={() => onNavigateToWork(work.workId)}
        aria-current={isCurrent ? "page" : undefined}
        className={classNames(
          "border-b border-gray-300 text-start text-sm transition-colors",
          isCurrent
            ? "bg-[#F6F4FF] shadow-[inset_3px_0_0_#6B4EFF] hover:bg-[#EFEBFF]"
            : "hover:bg-gray-100",
        )}>
        <td className={"w-[54px] pl-2"} onClick={(e) => e.stopPropagation()}>
          <div className={"flex items-center gap-1"}>
            {hasExpandedContent && (
              <button
                type={"button"}
                aria-expanded={isExpanded}
                aria-label={
                  isExpanded ? "Collapse work tasks" : "Expand work tasks"
                }
                className={
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#7C66F0] transition-colors hover:bg-gray-100"
                }
                onClick={handleToggleExpanded}>
                <ChevronDown
                  size={24}
                  className={classNames(
                    "transition-transform",
                    isExpanded ? "rotate-0" : "-rotate-90",
                  )}
                />
              </button>
            )}

            {showSelection && (
              <input
                className={
                  "checkbox scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
                }
                onChange={(e) =>
                  setArchiveWorks &&
                  (!e.target.checked
                    ? setArchiveWorks((prev) =>
                        prev.filter((i) => i.workId !== work.workId),
                      )
                    : setArchiveWorks((prev) => [...prev, work]))
                }
                type={"checkbox"}
                checked={archiveWorks?.some((i) => i.workId === work.workId)}
              />
            )}
          </div>
        </td>

        <td className={"max-w-[300px] px-4 py-2"}>
          <div className={"flex min-w-0 items-center gap-2"}>
            <div
              className={
                "text-purple w-fit min-w-0 max-w-full cursor-pointer truncate font-semibold hover:underline"
              }>
              <span className={"block min-w-0 truncate"}>{work.workName}</span>
              <div className={"truncate text-sm font-normal text-gray-500"}>
                {myWorkHome && filter
                  ? getDueDateWord(filter, work)
                  : getMessage(
                      (work?.taskCounts || 0) -
                        (work?.completedTaskCounts || 0),
                    )}
              </div>
            </div>
            {/* Centered beside the name and subtitle (owner, 2026-10-19). */}
            {isCurrent && (
              <span
                className={
                  "shrink-0 rounded-[6px] bg-[#6B4EFF] px-[7px] py-px text-[11px] font-bold leading-[16px] text-white"
                }>
                Current
              </span>
            )}
          </div>
        </td>

        {showRepeat && (
          <td
            className={
              "whitespace-nowrap px-4 py-2 font-semibold text-gray-800"
            }>
            {work?.repeatType && work?.repeatType !== "Never" && (
              <div
                data-tooltip-id={"repeat-type-tooltip"}
                data-tooltip-content={work.repeatType || "No repeat type"}
                className={
                  "flex h-5 w-5 items-center justify-center rounded-full bg-[#6938EF] text-sm text-white"
                }>
                <IoRepeat />
              </div>
            )}
          </td>
        )}

        <td
          className={"whitespace-nowrap px-4 py-2 font-semibold text-gray-800"}>
          {work.workType}
        </td>

        <td className={"max-w-[200px] truncate px-4 py-2"}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              const link = navigateToContact({
                type: work.businessContactId ? "organization" : "contact",
                id: work.businessContactId || "",
                customerId: work.customerId || "",
              });
              onNavigateToContact(link);
            }}
            className={
              "flex w-fit max-w-[200px] cursor-pointer items-center gap-2 truncate"
            }>
            <AvatarPlaceholder
              size={"2xs"}
              label={getInitialsFromFullName(work.customerName)}
              type={work.businessContactId ? "purple" : "blue"}
            />
            <div
              className={"font-semibold hover:text-[#7C66F0] hover:underline"}>
              {work.customerName}
            </div>
          </div>
        </td>

        <td
          className={"px-4 py-2"}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}>
          {work.jobStatus && (
            <StatusesDropdown
              workTypeId={work.workTypeId || ""}
              workId={work.workId || ""}
              positionType={worksCount - index < 4 ? undefined : "right-bottom"}
              triggerElement={
                <div
                  className={
                    "flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-[8px] border-[1px] border-gray-200 bg-white px-1 py-0.5 text-sm font-semibold text-gray-800"
                  }>
                  <div
                    className={"flex h-[10px] w-[10px] rounded-full"}
                    style={{
                      backgroundColor: getStatusColor(work.jobStatus || ""),
                    }}
                  />
                  <div>
                    {statusFilter &&
                    statusFilter.some((i) =>
                      ["Ready", "In Progress", "Waiting", "Completed"].includes(
                        i,
                      ),
                    )
                      ? work.jobStatus
                      : showStatus(work)}
                  </div>
                  <ChevronDown size={16} />
                </div>
              }
            />
          )}
        </td>

        <td className={"px-4 py-2 font-semibold text-gray-800"}>
          {moment(work.startDate).format("MMM DD, YYYY")}
        </td>

        <td
          className={classNames(
            "px-4 py-2 font-semibold",
            isOverdue(work) ? "text-orange-400" : "text-gray-800",
          )}>
          {moment(work.dueDate).format("MMM DD, YYYY")}
        </td>

        {showTaskProgress && (
          <td className={"w-[120px] px-4 py-2 text-center"}>
            <ProgressCell
              percent={getWorkProgress({
                taskCounts: work.taskCounts || 0,
                completedTaskCounts: work.completedTaskCounts || 0,
              })}
              color={TASK_PROGRESS_COLOR}
            />
          </td>
        )}

        {showTimeBudget && (
          <td className={"w-[120px] px-4 py-2 text-center"}>
            <TimeBudgetCell work={work} />
          </td>
        )}

        {showRole && (
          <td
            className={
              "max-w-[160px] truncate px-4 py-2 font-semibold text-gray-800"
            }>
            {work.roleName || "No role"}
          </td>
        )}

        <td className={"px-4 py-2"}>
          <AvatarStack
            withPreview
            previewTitle={"Assignee"}
            items={(work.assigneeList || []).map((item) => ({
              id: item.assignedUserId,
              label: getInitials(item.firstName, item.lastName),
              name: item.firstName + " " + item.lastName,
              color: item.color,
            }))}
          />
        </td>
      </tr>

      {isExpanded && hasExpandedContent && (
        <tr className={"border-b border-gray-300"}>
          <td colSpan={columnsCount} className={"px-4 py-3"}>
            <div>{renderExpandedContent?.(work)}</div>
          </td>
        </tr>
      )}
    </>
  );
};
